
import { catchError, distinctUntilChanged, filter, skipWhile, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { PaymentDisplayAggregateInfo } from '../../../services/payment-display-aggregate-info';
import { PaymentsService } from '../../../services/payments.service';
import { Observable } from 'rxjs';
import { DashboardFilters } from '../../dashboard/filters/dashboard-filters.model';
import { ProgressStates } from '../progress-states.model';
import { FiltersService } from '../../dashboard/filters/filters.service';
import { BaseObserver } from '../../../shared/baseobserver';
import { Util } from '../../../../util';

@Component({
  selector: 'ssg-at-a-glance-payments',
  templateUrl: './at-a-glance-payments.component.html',
  styleUrls: ['../at-a-glance.component.less']
})
export class AtAGlancePaymentsComponent extends BaseObserver implements OnInit {
  stateChanged = new EventEmitter<any>();
  @Output() progressStatesChanged = new EventEmitter<ProgressStates>();
  loadingPayments: boolean;
  paymentAggregateInfo: PaymentDisplayAggregateInfo;
  fileCountByDate: {date: Date; numberFilesSent: number} [];
  private _paymentsObservable: Observable<string | PaymentDisplayAggregateInfo>;
  private _allExpanded = false;
  private _sectionExpanded = false;

  constructor(private paymentsService: PaymentsService, private filtersService: FiltersService) {
    super();
  }

  ngOnInit() {
    this._clearPayments();
    this._initLoaders();
    this._subscribePayments();
  }

  private _initLoaders() {
    this._paymentsObservable = this.filtersService.getFilters()
      .pipe(
        filter((filt) => filt.payDate !== Util.MIN_DATE),
        distinctUntilChanged(DashboardFilters.compareFilters),
        tap(filters => {
          this.loadingPayments = true;
          this._clearPayments();
        }),
        switchMap((filters: DashboardFilters) => this.paymentsService.getPaymentInfo(filters.payGroups.map(p => p.code).join(','),
              filters.payDate, filters.getPaySequences()).pipe(
              catchError((error: any): string => (error || '' ).toString()))
            ),
        takeUntil(this.destroy$)
      );
  }

  private _subscribePayments() {
   this._paymentsObservable
      .subscribe((paymentData: string | PaymentDisplayAggregateInfo) => {
        if (typeof paymentData === 'string') { // Then there is an error
          this.loadingPayments = false;
        } else {
          this._processPayments(paymentData);
          this.loadingPayments = true;
        }
      });
  }

  private _processPayments(paymentData: PaymentDisplayAggregateInfo) {
    this.paymentAggregateInfo = paymentData;
    // create array of {string, num} (where string is a representation of a date) so that we can iterate through
    // and display dates in the HTML along with the number of files transferred on that date.
    // Note: maps are not iterable in typescript, which is why we put the map entries into an array
    this.fileCountByDate = Array.from(
        this.paymentAggregateInfo.paymentFileInfos
        .map(info => info.sentUtcDate.toString())
        .reduce((countByDate, dt) => {
          const curCount = (countByDate.get(dt) != null) ? countByDate.get(dt) : 0;
          countByDate.set(dt, curCount + 1);
          return countByDate;
        }, new Map<string, number>())
        .entries()
      )
      .map((pair) => ({date: new Date(pair[0]), numberFilesSent: pair[1]}))
      .sort((a, b) =>  // sort string representations of dates: show most recent first, least recent last
         (a.date < b.date) ? 1 : (a.date > b.date) ? -1 : 0
      );

    const noUnpaidPayments = this.paymentAggregateInfo.possibleOutstandingPaymentFiles === false;
    const atLeastOneDirectDepositExists = this.paymentAggregateInfo.paymentFileInfos.length > 0;
    this.progressStatesChanged.emit({
      complete: noUnpaidPayments && atLeastOneDirectDepositExists,
      inProgress: !noUnpaidPayments && atLeastOneDirectDepositExists
    });
  }

  private _clearPayments() {
    this.paymentAggregateInfo = new PaymentDisplayAggregateInfo(false, false, []);
  }
  get allExpanded(): boolean {
    return this._allExpanded;
  }

  @Input() set allExpanded(value: boolean) {
    if (value === this._allExpanded) {
      return;
    }

    this._allExpanded = value;
    this.sectionExpanded = value;
    this.stateChanged.emit();
  }

  get sectionExpanded(): boolean {
    return this._sectionExpanded;
  }

  @Input() set sectionExpanded(value: boolean) {
    if (value === this._sectionExpanded) {
      return;
    }

    this._sectionExpanded = value;
    this.stateChanged.emit();
  }

}
